import ArticleLayout from '@/components/feature/ArticleLayout';
import { useLang } from '@/hooks/useLang';

export default function AllChinaBuySpreadsheetPage() {
  const currentLang = useLang();

  return (
    <ArticleLayout
      title="AllChinaBuy Spreadsheet Complete Guide 2026 — Products, Sellers & Links"
      metaDesc="Complete guide to the AllChinaBuy Spreadsheet. Discover product categories, seller resources, how to search, QC guides, shipping tips and buying strategies for 2026."
      h1="AllChinaBuy Spreadsheet Complete Guide 2026"
      author="Editorial Team"
      publishedDate="2026-01-15"
      updatedDate="2026-08-26"
      breadcrumbs={[
        { label: 'Home', href: `/${currentLang}/` },
        { label: 'Spreadsheet', href: `/${currentLang}/allchinabuy-spreadsheet/` },
      ]}
    >
      <div className="prose max-w-none">
        {/* Direct Answer */}
        <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-10">
          <p className="text-lg text-foreground-800 leading-relaxed m-0">
            The <strong>AllChinaBuy Spreadsheet</strong> is an organized resource hub that helps buyers research products, sellers, shipping options, and quality checks before making purchases. It provides structured product categories, seller information, and practical buying guides to help you make informed decisions.
          </p>
        </div>

        {/* Table of Contents */}
        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-12 mb-4">Table of Contents</h2>
        <nav className="bg-background-100 border border-background-200/70 rounded-lg p-5 mb-10">
          <ul className="space-y-2 text-sm">
            <li><a href="#what-is" className="text-primary-600 hover:text-primary-700">What is the AllChinaBuy Spreadsheet?</a></li>
            <li><a href="#categories" className="text-primary-600 hover:text-primary-700">Product Categories in the Spreadsheet</a></li>
            <li><a href="#how-to-use" className="text-primary-600 hover:text-primary-700">How to Use the AllChinaBuy Spreadsheet</a></li>
            <li><a href="#seller-research" className="text-primary-600 hover:text-primary-700">Seller Research Guide</a></li>
            <li><a href="#qc-guide" className="text-primary-600 hover:text-primary-700">QC & Quality Check Guide</a></li>
            <li><a href="#shipping" className="text-primary-600 hover:text-primary-700">Shipping & Cost Factors</a></li>
            <li><a href="#comparison" className="text-primary-600 hover:text-primary-700">Comparison: What the Spreadsheet Covers</a></li>
            <li><a href="#safety" className="text-primary-600 hover:text-primary-700">Safety & Risk Awareness</a></li>
            <li><a href="#faq" className="text-primary-600 hover:text-primary-700">Frequently Asked Questions</a></li>
            <li><a href="#related" className="text-primary-600 hover:text-primary-700">Related Resources</a></li>
          </ul>
        </nav>

        {/* What is */}
        <h2 id="what-is" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">What is the AllChinaBuy Spreadsheet?</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          The AllChinaBuy Spreadsheet is a research tool designed for buyers who want to explore products available through buying-agent platforms. Rather than browsing scattered links across forums and social media, the spreadsheet organizes product categories, seller references, and buying resources into a single, searchable format.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Whether you are looking for shoes, clothing, accessories, or electronics, the spreadsheet provides a starting point for your research. It does not guarantee product availability, seller reliability, or transaction outcomes — but it offers a structured way to begin exploring your options.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          For the complete product catalog, you can explore the <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">full AllChinaBuy Spreadsheet</a> on our main website.
        </p>

        {/* Categories */}
        <h2 id="categories" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Product Categories in the Spreadsheet</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          The spreadsheet covers a wide range of product categories. Here are the main categories you can explore:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {[
            ['Shoes', 'https://kaspreadsheet.com/category-911-Shoes.html'],
            ['Hoodies & Sweaters', 'https://kaspreadsheet.com/search-912/name/Shoes.html'],
            ['T-Shirts', 'https://kaspreadsheet.com/search-913/name/Shoes.html'],
            ['Jackets', 'https://kaspreadsheet.com/search-914/name/Shoes.html'],
            ['Pants & Shorts', 'https://kaspreadsheet.com/search-915/name/Shoes.html'],
            ['Headwear', 'https://kaspreadsheet.com/search-916/name/Shoes.html'],
            ['Sets', 'https://kaspreadsheet.com/search-917/name/Shoes.html'],
            ['Underwear', 'https://kaspreadsheet.com/search-918/name/Shoes.html'],
            ['Jerseys', 'https://kaspreadsheet.com/search-919/name/Shoes.html'],
            ['Accessories', 'https://kaspreadsheet.com/search-920/name/Shoes.html'],
            ['Bags', 'https://kaspreadsheet.com/search-921/name/Shoes.html'],
            ['Belts', 'https://kaspreadsheet.com/search-922/name/Shoes.html'],
            ['Perfumes', 'https://kaspreadsheet.com/search-923/name/Shoes.html'],
            ['Other Products', 'https://kaspreadsheet.com/search-924/name/Shoes.html'],
          ].map(([name, url]) => (
            <a
              key={name}
              href={url}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-4 py-3 bg-background-100 border border-background-200/70 rounded-md hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm font-medium text-foreground-800"
            >
              <i className="ri-arrow-right-line text-primary-500" />
              {name}
            </a>
          ))}
        </div>

        {/* How to use */}
        <h2 id="how-to-use" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">How to Use the AllChinaBuy Spreadsheet</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Using the AllChinaBuy Spreadsheet effectively requires a systematic approach. Here is a step-by-step guide to help you navigate the resources:
        </p>
        <ol className="list-decimal list-inside space-y-3 text-foreground-700 mb-6 pl-1">
          <li><strong>Identify your product category</strong> — Start by selecting the category that matches what you are looking for, such as shoes, clothing, or accessories.</li>
          <li><strong>Review seller information</strong> — Look at seller references and cross-check with available reviews or feedback when possible.</li>
          <li><strong>Compare product details</strong> — Note product specifications, sizing information, and available variations.</li>
          <li><strong>Research QC practices</strong> — Understand how quality checks work and what photos or information you can request.</li>
          <li><strong>Calculate shipping estimates</strong> — Consider destination, weight, and shipping method when estimating total costs.</li>
          <li><strong>Verify current availability</strong> — Product availability and pricing change frequently. Always confirm before purchasing.</li>
        </ol>

        {/* Seller Research */}
        <h2 id="seller-research" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Seller Research Guide</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Researching sellers is a critical part of the buying process. The spreadsheet provides seller references, but buyers should conduct additional verification before making purchases. Consider the following factors when evaluating sellers:
        </p>
        <ul className="list-disc list-inside space-y-2 text-foreground-700 mb-6 pl-1">
          <li>How long the seller has been active in the marketplace</li>
          <li>Available customer feedback and review patterns</li>
          <li>Return and exchange policies</li>
          <li>Communication responsiveness</li>
          <li>Product range consistency</li>
          <li>Price positioning relative to similar products</li>
        </ul>

        {/* QC Guide */}
        <h2 id="qc-guide" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">QC & Quality Check Guide</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Quality Check (QC) is an essential step when purchasing through buying-agent platforms. QC photos allow you to inspect products before they are shipped to your destination. Understanding how to read and evaluate QC photos can help you avoid disappointment and make more informed decisions.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Common QC factors include stitching quality, material accuracy, color matching, logo placement, and overall construction. Requesting detailed QC photos from multiple angles is recommended whenever possible.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          For a deeper dive into QC practices, read our <a href="/${currentLang}/allchinabuy-legit/" className="text-primary-600 hover:text-primary-700 font-medium">Safety & Legitimacy Guide</a>.
        </p>

        {/* Shipping */}
        <h2 id="shipping" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Shipping & Cost Factors</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Shipping costs and delivery times depend on multiple variables. Understanding these factors can help you plan purchases more effectively and avoid unexpected expenses. Key factors include:
        </p>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
            <thead className="bg-background-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Factor</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Impact</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {[
                ['Destination country', 'Different carriers and routes affect cost and speed'],
                ['Parcel weight', 'Heavier items cost more to ship across all methods'],
                ['Parcel dimensions', 'Oversized items may incur dimensional weight charges'],
                ['Shipping method', 'Express vs. standard vs. economy have different trade-offs'],
                ['Insurance', 'Optional insurance adds cost but provides protection'],
                ['Customs duties', 'Destination country may apply import taxes and fees'],
              ].map(([factor, impact]) => (
                <tr key={factor} className="bg-background-50">
                  <td className="px-4 py-3 font-medium text-foreground-800">{factor}</td>
                  <td className="px-4 py-3 text-foreground-600">{impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-foreground-700 leading-relaxed mb-6">
          For more detailed shipping information, visit our <a href="/${currentLang}/allchinabuy-shipping/" className="text-primary-600 hover:text-primary-700 font-medium">Shipping Guide</a>.
        </p>

        {/* Comparison */}
        <h2 id="comparison" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Comparison: What the Spreadsheet Covers</h2>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
            <thead className="bg-background-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Resource</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">Product Discovery</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">Seller Info</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">QC Guides</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">Shipping Info</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">Safety</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {[
                ['AllChinaBuy Spreadsheet', '✓', '✓', 'Partial', '✓', 'Partial'],
                ['Forum Discussions', '✓', 'Partial', 'Partial', 'Partial', '—'],
                ['Social Media', '✓', '—', '—', '—', '—'],
                ['Our Resource Hub', '✓', '✓', '✓', '✓', '✓'],
              ].map(([name, ...vals]) => (
                <tr key={name} className="bg-background-50">
                  <td className="px-4 py-3 font-medium text-foreground-800">{name}</td>
                  {vals.map((v, i) => (
                    <td key={i} className="px-4 py-3 text-center text-foreground-600">{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Safety */}
        <h2 id="safety" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Safety & Risk Awareness</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Before using any buying-agent platform, it is important to understand the risks involved. No platform can guarantee product authenticity, seller reliability, or shipping outcomes. The following precautions are recommended:
        </p>
        <ul className="list-disc list-inside space-y-2 text-foreground-700 mb-4 pl-1">
          <li>Research sellers independently before making purchases</li>
          <li>Request QC photos before shipping</li>
          <li>Understand return and dispute policies</li>
          <li>Use secure payment methods with buyer protection</li>
          <li>Be aware of customs regulations in your destination country</li>
          <li>Start with smaller purchases to test seller reliability</li>
        </ul>
        <p className="text-foreground-700 leading-relaxed mb-6">
          Read our full <a href="/${currentLang}/allchinabuy-legit/" className="text-primary-600 hover:text-primary-700 font-medium">Safety & Legitimacy Guide</a> for detailed risk awareness information.
        </p>

        {/* FAQ */}
        <h2 id="faq" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4 mb-10">
          {[
            {
              q: 'Is the AllChinaBuy Spreadsheet free to use?',
              a: 'Yes, the spreadsheet resource is available for research purposes. However, actual purchases and transactions are handled through the respective buying-agent platforms.',
            },
            {
              q: 'How often is the spreadsheet updated?',
              a: 'Product availability and seller information change frequently. We update our guides regularly, but you should always verify current information directly with sellers before purchasing.',
            },
            {
              q: 'Can I trust the sellers listed in the spreadsheet?',
              a: 'The spreadsheet provides references for research purposes. We recommend conducting independent seller verification, reading reviews, and starting with small test orders.',
            },
            {
              q: 'What should I do if a product arrives with quality issues?',
              a: 'Contact the seller and the buying-agent platform immediately. Document the issue with photos and refer to the platform\'s dispute resolution process.',
            },
            {
              q: 'How do I calculate total shipping costs?',
              a: 'Shipping costs depend on destination, weight, dimensions, and method. Use the shipping calculator tools provided by the buying-agent platform and factor in potential customs duties.',
            },
          ].map((faq, i) => (
            <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg p-5">
              <h3 className="font-semibold text-foreground-900 mb-2">{faq.q}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>

        {/* Related Resources */}
        <h2 id="related" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Related Resources</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          <a href="/${currentLang}/allchinabuy-review/" className="flex items-center gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors">
            <i className="ri-file-text-line text-primary-500 text-lg" />
            <span className="text-sm font-medium text-foreground-800">AllChinaBuy Review</span>
          </a>
          <a href="/${currentLang}/allchinabuy-shipping/" className="flex items-center gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors">
            <i className="ri-truck-line text-primary-500 text-lg" />
            <span className="text-sm font-medium text-foreground-800">Shipping Guide</span>
          </a>
          <a href="/${currentLang}/allchinabuy-coupon/" className="flex items-center gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors">
            <i className="ri-price-tag-line text-primary-500 text-lg" />
            <span className="text-sm font-medium text-foreground-800">Coupon Guide</span>
          </a>
          <a href="/${currentLang}/allchinabuy-warehouse/" className="flex items-center gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors">
            <i className="ri-archive-line text-primary-500 text-lg" />
            <span className="text-sm font-medium text-foreground-800">Warehouse Guide</span>
          </a>
        </div>

        {/* CTA */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-8 text-center">
          <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
            Ready to Explore the AllChinaBuy Spreadsheet?
          </h3>
          <p className="text-sm text-foreground-600 mb-5">
            Visit our main website for the complete product catalog and latest resources.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors"
          >
            Explore AllChinaBuy Spreadsheet →
          </a>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-6 border-t border-background-200/70">
          <p className="text-xs text-foreground-500 leading-relaxed">
            <strong>Disclaimer:</strong> This website provides general educational information. It does not guarantee seller quality, product authenticity, shipping outcomes, refunds or transaction results. Always verify current information before purchasing. The AllChinaBuy Spreadsheet is a research resource only — actual purchases are handled through third-party platforms.
          </p>
        </div>
      </div>
    </ArticleLayout>
  );
}